import { MIMEType } from 'node:util';
import getLogger, { type Logger } from '../logger.ts';
import { ALL, createMimeType, isCompatibleWith } from '../utils/mime-type-utils.ts';
import { getLogPrefix, isLoggingSuppressed } from './hints.ts';
import type { Encoder } from './types.ts';

export abstract class AbstractEncoder<T> implements Encoder<T> {
    #encodableMimeTypes: readonly MIMEType[];
    protected logger: Logger = getLogger('codec.encoder');

    protected constructor(...supportedMimeTypes: MIMEType[]) {
        this.#encodableMimeTypes = [...supportedMimeTypes];
    }

    setLogger(logger: Logger) {
        this.logger = logger;
    }

    encodableMimeTypes(): readonly MIMEType[] {
        return this.#encodableMimeTypes;
    }

    canEncode(type, mimeType?: MIMEType): boolean {
        if (mimeType === undefined) {
            return true;
        }
        for (const supportedMimeType of this.#encodableMimeTypes) {
            if (isCompatibleWith(mimeType, supportedMimeType)) {
                return true;
            }
        }
        return false;
    }

    abstract encode(input: Promise<T> | T, mimeType?: MIMEType, hints?: Map<string, unknown>): Promise<BlobPart>;
}

export class BlobEncoder extends AbstractEncoder<BlobPart> {
    constructor() {
        super(ALL);
    }

    canEncode(type, mimeType?: MIMEType): boolean {
        return super.canEncode(type, mimeType);
    }

    async encode(input: Promise<BlobPart> | BlobPart, mimeType?: MIMEType, hints?: Map<string, unknown>): Promise<BlobPart> {
        const part = await input;
        if (this.logger.enabledFor('debug') && !isLoggingSuppressed(hints)) {
            this.#logValue(part);
        }
        return part;
    }

    #logValue(part: BlobPart, hints?: Map<string, unknown>): void {
        const prefix = getLogPrefix(hints);
        this.logger.debug(`${prefix}Encoding value of type ${typeof part} with size ${part['byteLength'] ?? part['size'] ?? part['length']}`);
    }
}


export class StringEncoder extends AbstractEncoder<string> {
    private constructor(...mimeTypes: MIMEType[]) {
        super(...mimeTypes);
    }

    canEncode(type, mimeType?: MIMEType): boolean {
        return super.canEncode(type, mimeType) && type === 'string';
    }

    async encode(input: Promise<string> | string, mimeType?: MIMEType, hints?: Map<string, unknown>): Promise<BlobPart> {
        input = await input;
        if (!isLoggingSuppressed(hints) && this.logger.enabledFor('debug')) {
            const logPrefix = getLogPrefix(hints);
            const traceEnabled = this.logger.enabledFor('trace');
            this.logger.debug(`${logPrefix}Writing ${traceEnabled ? `${input}` : `${input.substring(0, 100)}...`}`);
        }
        return input;
    }

    static textPlainOnly(): StringEncoder {
        return new StringEncoder(createMimeType('text','plain', 'utf-8'));
    }
    static allMimeTypes(): StringEncoder {
        return new StringEncoder(createMimeType('text', 'plain', 'utf-8'), ALL);
    }
}
